'use client'

import { CSSProperties, ReactNode, useCallback, useEffect, useRef } from 'react'

import { cn, fireEvent, passPropsToSingleChild } from '@/shared/lib'

export const STICKY_EVENT = 'sticky-change'

const fireStickyEvent = (stuck: boolean, target: HTMLElement) => {
  fireEvent(STICKY_EVENT, { detail: { stuck, target } })
}

export const StickyContainer = ({
  className,
  offset = 0,
  children,
  style,
}: {
  className?: string
  offset?: number
  children: ReactNode
  style?: CSSProperties
}) => {
  const sentinelRef = useRef<HTMLDivElement>(null)
  const targetRef = useRef<HTMLDivElement>(null)

  const handleObserve = useCallback((records: IntersectionObserverEntry[]) => {
    if (!targetRef.current) return

    for (const record of records) {
      const target = record.boundingClientRect
      const { rootBounds } = record

      if (!rootBounds) return

      const { bottom } = target

      // sticking 시작
      if (bottom < rootBounds.top) {
        fireStickyEvent(true, targetRef.current)
      }

      // sticking 종료
      if (bottom >= rootBounds.top && bottom < rootBounds.bottom) {
        fireStickyEvent(false, targetRef.current)
      }
    }
  }, [])

  useEffect(() => {
    const observer = new IntersectionObserver(handleObserve, {
      threshold: [0],
      root: null,
    })

    const sentinel = sentinelRef.current

    if (sentinel) {
      observer.observe(sentinel)
    }

    return () => {
      if (sentinel) {
        observer.unobserve(sentinel)
      }
    }
  }, [handleObserve])

  return (
    <div className={cn('z-sticky sticky top-0 h-fit', className)} style={style}>
      <div
        ref={sentinelRef}
        className="invisible absolute h-1 w-full"
        style={{
          bottom: `calc(100% + ${offset + 1}px)`,
        }}
      />
      {passPropsToSingleChild(children, { ref: targetRef })}
    </div>
  )
}
